import React, { useState } from 'react'
import { Stack, Typography, Badge, Button, Tooltip, IconButton } from '@mui/material'
import { Portal } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import DoctorIcon from '@/components/ui/icons/Doctor'
import NetworkSpeed from './NetworkSpeed'
import TopbarButton from '../../../layouts/diagnostic/TopbarButton'

import { useTranslation } from 'react-i18next'
import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import dayjs from 'dayjs'
import EmergencyDoctorSelectionDialog from './EmergencyDoctorSelectionDialog'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import { useInterval } from 'usehooks-ts'
import NurseIcon from '@/components/ui/icons/Nurse'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VideocamIcon from '@mui/icons-material/Videocam'
import _ from 'lodash'
import { Consultation } from '@/core/models/consultation.model'
import { Staff } from '@/core/models/staff.model'

interface TopbarProps {
  authUser: any
  consultation: Consultation | undefined
  roomCreator: string | Staff
  handleLeaveRoom: () => void
  toggleInvitationDialog: () => void
  isInfo: boolean
  setIsInfo: React.Dispatch<React.SetStateAction<boolean>>
}

export const MobileEmergencyTopbar = ({ authUser, consultation, roomCreator, handleLeaveRoom, toggleInvitationDialog, isInfo, setIsInfo }: TopbarProps) => {
  const { topbarContentContainer } = useDiagnosticLayout()
  const { t } = useTranslation()

  const [currentTime, setCurrentTime] = useState(dayjs())

  useInterval(() => {
    setCurrentTime(dayjs())
  }, 60 * 1000)

  const [isExpanded, setIsExpanded] = useState(false)

  const isAuthUserPDS = authUser?.status === 'PDS'
  const isRoomCreator = authUser?._id === (typeof roomCreator === 'string' ? roomCreator : roomCreator?._id)
  const isConsultationValid = !!consultation?._id

  return (
    <Portal container={topbarContentContainer}>
      <Stack direction="column" alignItems="center" sx={{ width: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%', backgroundColor: 'primary.main', p: 1 }}>
          <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '16px', fontWeight: 500 }}>
            {t('page.emergency.title')}
          </Typography>
          {isAuthUserPDS && isRoomCreator && isConsultationValid && (
            <TopbarButton variant="contained" onClick={toggleInvitationDialog}>
              <PersonAddAltIcon htmlColor="#FFF" />
            </TopbarButton>
          )}
          <TopbarButton id="switch-mode" onClick={() => setIsInfo(!isInfo)} variant="contained">
            {isInfo ? <VideocamIcon /> : <InfoOutlinedIcon />}
          </TopbarButton>
          <IconButton onClick={() => setIsExpanded(!isExpanded)} sx={{ color: 'white' }}>
            {isExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconButton>
        </Stack>
        {isExpanded && (
          <Stack direction="column" alignItems="center" sx={{ width: '100%', backgroundColor: 'primary.dark', p: 1 }} spacing={2}>
            {isAuthUserPDS && !!consultation?.medecin && (
              <>
                <HorizontalRuleRoundedIcon />
                <Stack alignItems="center" justifyContent="center" direction="row" spacing={1}>
                  <DoctorIcon htmlColor="#FFF" />
                  <Typography variant="h2" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                    {`${consultation.medecin.userInfo?.name} ${consultation.medecin.userInfo?.surname}`}
                  </Typography>
                  <TimerOutlinedIcon />
                  <Typography component="p" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                    {dayjs.duration(Math.abs(currentTime.diff(dayjs(consultation.startDate)))).format('HH[h]mm')}
                  </Typography>
                </Stack>
              </>
            )}
            {authUser?.status === 'MEDECIN' && !!consultation && (
              <>
                <HorizontalRuleRoundedIcon />
                <Stack alignItems="center" justifyContent="center" direction="row" spacing={1}>
                  <NurseIcon htmlColor="#FFF" />
                  <Typography variant="h2" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                    {_.trim(`${consultation.pds?.userInfo?.name || ''} ${consultation.pds?.userInfo?.surname || ''}`)}
                  </Typography>
                  {!!consultation.medecin && (
                    <>
                      <DoctorIcon htmlColor="#FFF" />
                      <Typography variant="h2" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                        {`${consultation.medecin.userInfo?.name} ${consultation.medecin.userInfo?.surname}`}
                      </Typography>
                    </>
                  )}
                  <TimerOutlinedIcon />
                  <Typography component="p" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                    {dayjs.duration(Math.abs(currentTime.diff(dayjs(consultation.startDate)))).format('HH[h]mm')}
                  </Typography>
                </Stack>
              </>
            )}
            <Stack direction="row" spacing={3}>
              {isAuthUserPDS && isRoomCreator && isConsultationValid && (
                <EmergencyDoctorSelectionDialog consultationId={consultation._id} title={t('page.emergency.doctorSelectionDialog.title')} />
              )}
              <NetworkSpeed />
              <Tooltip title={t('page.emergency.leaveRoomButton.tooltip')}>
                <IconButton onClick={handleLeaveRoom}>
                  <ExitToAppIcon htmlColor="#FFF" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Portal>
  )
}
